import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiJson } from "@/data/api";

export interface Device {
  id: string;
  name: string;
  last_seen_at: number | null;
  created_at: number;
  ambient_capture_enabled: boolean;
  voice_activation_enabled: boolean;
  active_capture_session_id: string | null;
  active_capture_mode: number | null;
  active_capture_started_at: number | null;
}

interface RawDevice extends Omit<
  Device,
  | "last_seen_at"
  | "created_at"
  | "active_capture_started_at"
  | "active_capture_mode"
> {
  last_seen_at: number | string | null;
  created_at: number | string;
  active_capture_mode: number | string | null;
  active_capture_started_at: number | string | null;
}

const deviceKeys = {
  all: ["devices"] as const,
  list: (workspaceSlug: string | null | undefined) =>
    [...deviceKeys.all, "list", workspaceSlug ?? null] as const,
};

export function useDevicesQuery(workspaceSlug: string | null | undefined) {
  return useQuery({
    queryKey: deviceKeys.list(workspaceSlug),
    queryFn: () => listDevices(workspaceSlug ?? ""),
    enabled: Boolean(workspaceSlug),
    refetchInterval: 30_000,
    staleTime: 10_000,
  });
}

export function useDeviceMutation(workspaceSlug: string | null | undefined) {
  const queryClient = useQueryClient();
  const invalidateDevices = () =>
    queryClient.invalidateQueries({ queryKey: deviceKeys.list(workspaceSlug) });

  return {
    updateCaptureSettings: useMutation({
      mutationFn: (input: {
        deviceId: string;
        settings: Partial<
          Pick<Device, "ambient_capture_enabled" | "voice_activation_enabled">
        >;
      }) => updateCaptureSettings(input.deviceId, input.settings),
      onSuccess: invalidateDevices,
    }),
    startExplicitCapture: useMutation({
      mutationFn: startExplicitCapture,
      onSuccess: invalidateDevices,
    }),
    stopExplicitCapture: useMutation({
      mutationFn: (input: {
        deviceId: string;
        captureSessionId?: string | null;
      }) => stopExplicitCapture(input.deviceId, input.captureSessionId),
      onSuccess: invalidateDevices,
    }),
  };
}

export async function listDevices(workspaceSlug: string): Promise<Device[]> {
  const body = await apiJson<{ devices: RawDevice[] }>(
    `/devices?workspace_slug=${encodeURIComponent(workspaceSlug)}`,
  );
  return (body.devices ?? []).map(normalizeDevice);
}

function updateCaptureSettings(
  deviceId: string,
  settings: Partial<
    Pick<Device, "ambient_capture_enabled" | "voice_activation_enabled">
  >,
) {
  return apiJson<{ device?: Device }>(
    `/devices/${encodeURIComponent(deviceId)}/capture-settings`,
    {
      method: "PATCH",
      json: settings,
    },
  );
}

function startExplicitCapture(deviceId: string) {
  return apiJson<{ capture_session?: { id: string } }>(
    `/devices/${encodeURIComponent(deviceId)}/captures/start`,
    {
      method: "POST",
      json: { start_trigger: "web" },
    },
  );
}

function stopExplicitCapture(
  deviceId: string,
  captureSessionId?: string | null,
) {
  return apiJson<{ capture_session?: { id: string } }>(
    `/devices/${encodeURIComponent(deviceId)}/captures/stop`,
    {
      method: "POST",
      json: {
        capture_session_id: captureSessionId ?? undefined,
        end_reason: "web",
      },
    },
  );
}

function normalizeDevice(device: RawDevice): Device {
  return {
    ...device,
    last_seen_at: toTimestampMs(device.last_seen_at),
    created_at: toTimestampMs(device.created_at) ?? 0,
    active_capture_started_at: toTimestampMs(device.active_capture_started_at),
    ambient_capture_enabled: Boolean(device.ambient_capture_enabled),
    voice_activation_enabled: Boolean(device.voice_activation_enabled),
    active_capture_session_id: device.active_capture_session_id ?? null,
    active_capture_mode:
      device.active_capture_mode == null
        ? null
        : Number(device.active_capture_mode),
  };
}

function toTimestampMs(
  value: number | string | null | undefined,
): number | null {
  if (value == null) return null;
  if (typeof value === "number") return value;
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
